import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

function RepositoryCreate({ accessToken }) {
  const [repoName, setRepoName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Validate token on component mount
    if (!accessToken) {
      console.error('No access token available');
      navigate('/login');
    }
  }, [accessToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      if (!accessToken) {
        throw new Error('No access token available. Please log in again.');
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/repositories`, 
        { 
          name: repoName.trim(), 
          description: description.trim(), 
          private: isPrivate,
          auto_init: true
        },
        { 
          headers: { 
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      console.log('Repository created successfully:', response.data);
      setSuccess(`Repository ${response.data.name} created successfully! Redirecting...`);
      
      // Reset form
      setRepoName('');
      setDescription('');
      setIsPrivate(false);

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard', { 
          state: { 
            message: `Repository ${response.data.name} created successfully!`,
            type: 'success'
          }
        });
      }, 2000);
    } catch (error) {
      console.error('Repository creation error:', error);
      
      if (error.response) {
        // Server responded with an error
        setError(error.response.data?.error || 'Failed to create repository. Please try again.');
      } else if (error.request) {
        // Request was made but no response received
        setError('Unable to reach the server. Please check your connection.');
      } else {
        // Something else went wrong
        setError(error.message || 'An unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', padding: '20px 0' }}>
      <Card style={{ width: '400px' }}>
        <Card.Header className="bg-primary text-white">
          <h3 className="mb-0">Create New Repository</h3>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Repository Name *</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="Enter repository name" 
                value={repoName}
                onChange={(e) => setRepoName(e.target.value)}
                required 
                disabled={isLoading}
                pattern="[A-Za-z0-9_-]+"
                title="Repository name can only contain letters, numbers, hyphens, and underscores"
              />
              <Form.Text className="text-muted">
                Only letters, numbers, hyphens, and underscores are allowed
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control 
                as="textarea"
                rows={3}
                placeholder="Enter repository description" 
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isLoading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check 
                type="checkbox" 
                label="Private Repository" 
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                disabled={isLoading}
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={isLoading || !repoName.trim()}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Creating...
                  </>
                ) : (
                  'Create Repository'
                )}
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/dashboard')}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default RepositoryCreate;
